import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    DataGridPro,
    GridActionsCellItem,
    GridColDef,
    GridRowParams,
} from '@mui/x-data-grid-pro'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import { DateTime } from 'luxon'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import useSession from '@hooks/useSession'
import ApiInteractor from '@utils/api-interactor'
import { resolveHaServerUrl } from '@utils/haServer'
import { describeError } from '@utils/errors'

/**
 * Who can use the Staff Console.
 *
 * Access is two things in two systems: a Habitat account marked as
 * staff, and a row saying what they may do. Adding someone here does
 * both — Habitat Admin creates the account and emails them a link to
 * set their own password, so no password is ever typed by whoever
 * grants the access.
 *
 * Removing someone takes their console access away but deliberately
 * leaves their Habitat account alone. Someone leaving the company is a
 * wider action than losing this tool, and it belongs in offboarding
 * rather than behind a row-level delete button.
 */
const STAFF_USERS_URL = `${resolveHaServerUrl() ?? ''}/v2/staff/users`

const ROLES = [
    'superadmin',
    'admin',
    'finance',
    'operations',
    'support',
    'studentsupport',
    'marketing',
    'design',
] as const

type Role = (typeof ROLES)[number]

type StaffUser = {
    id: string
    email: string
    role: Role
    created_by: string | null
    created_at: string
    updated_at: string
}

const EMPTY_FORM = {
    email: '',
    first_name: '',
    last_name: '',
    role: 'support' as Role,
}

export default function StaffAccessPage() {
    const session = useSession()!

    const [rows, setRows] = useState<StaffUser[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const [addOpen, setAddOpen] = useState(false)
    const [form, setForm] = useState(EMPTY_FORM)
    const [pendingRemoval, setPendingRemoval] = useState<StaffUser | null>(null)

    const load = useCallback(async () => {
        const res =
            await ApiInteractor._getWithJwt<StaffUser[]>(STAFF_USERS_URL)

        if (!res.ok) {
            setError(describeError(res, 'Could not load staff access'))
            return
        }

        setError(null)
        setRows(res.value)
    }, [])

    useEffect(() => {
        load()
    }, [load])

    async function handleAdd() {
        setBusy(true)

        const res = await ApiInteractor._postWithJwt<
            StaffUser & { accountCreated: boolean; emailSent: boolean }
        >(STAFF_USERS_URL, form)

        setBusy(false)

        if (!res.ok) {
            enqueueSnackbar(describeError(res, 'Could not grant access'), {
                variant: 'error',
            })
            return
        }

        /**
         * The account is made either way, so this stays a success —
         * but a failed setup email leaves them unable to sign in, and
         * saying a link is on its way when it is not means nobody
         * chases it until the person says they never got one.
         */
        if (res.value.accountCreated && !res.value.emailSent) {
            enqueueSnackbar(
                `${form.email} can now use the console, but the setup email did not send — ask them to use "forgotten password" to set one`,
                // Longer than the default — there is something to do
                // about this one, and `SnackbarProvider` here has no
                // dismiss button, so it must time out on its own.
                { variant: 'warning', autoHideDuration: 15000 }
            )
        } else {
            enqueueSnackbar(
                res.value.accountCreated
                    ? `${form.email} can now use the console — a setup email is on its way`
                    : `${form.email} can now use the console`,
                { variant: 'success' }
            )
        }

        setAddOpen(false)
        setForm(EMPTY_FORM)
        await load()
    }

    async function handleRoleChange(user: StaffUser, role: Role) {
        const res = await ApiInteractor._patchWithJwt(
            `${STAFF_USERS_URL}/${user.id}`,
            { role }
        )

        if (!res.ok) {
            enqueueSnackbar(describeError(res, 'Could not change role'), {
                variant: 'error',
            })
            return
        }

        enqueueSnackbar(`${user.email} is now ${role}`, {
            variant: 'success',
        })
        await load()
    }

    async function handleRemove() {
        if (!pendingRemoval) return

        setBusy(true)

        const res = await ApiInteractor._deleteWithJwt(
            `${STAFF_USERS_URL}/${pendingRemoval.id}`
        )

        setBusy(false)

        if (!res.ok) {
            enqueueSnackbar(describeError(res, 'Could not remove access'), {
                variant: 'error',
            })
            return
        }

        enqueueSnackbar(`${pendingRemoval.email} no longer has access`, {
            variant: 'success',
        })
        setPendingRemoval(null)
        await load()
    }

    const columns: GridColDef<StaffUser>[] = useMemo(
        () => [
            { field: 'email', headerName: 'Email', flex: 2, minWidth: 260 },
            {
                field: 'role',
                headerName: 'Role',
                flex: 1,
                minWidth: 180,
                renderCell: (params) => {
                    // Changing your own role is refused server-side, so
                    // don't offer it here either.
                    const isSelf = params.row.id === session.self.accessId

                    return (
                        <TextField
                            select
                            size="small"
                            value={params.row.role}
                            disabled={isSelf}
                            onChange={(e) =>
                                handleRoleChange(
                                    params.row,
                                    e.target.value as Role
                                )
                            }
                            sx={{ width: '100%' }}
                        >
                            {ROLES.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        </TextField>
                    )
                },
            },
            {
                field: 'created_by',
                headerName: 'Added by',
                flex: 1,
                minWidth: 220,
                valueGetter: (value) => value ?? '—',
            },
            {
                field: 'created_at',
                headerName: 'Added',
                flex: 1,
                minWidth: 160,
                valueFormatter: (value: string) =>
                    DateTime.fromISO(value).toFormat('dd LLL yyyy'),
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 60,
                getActions: (params: GridRowParams<StaffUser>) =>
                    params.row.id === session.self.accessId
                        ? []
                        : [
                              <GridActionsCellItem
                                  key="remove"
                                  icon={<DeleteIcon />}
                                  label="Remove access"
                                  onClick={() => setPendingRemoval(params.row)}
                              />,
                          ],
            },
        ],
        // `session` is stable for the life of the page; re-creating the
        // columns on every render would remount each role dropdown and
        // close it mid-selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    // The nav entry is disabled for everyone else, but the URL is still
    // typeable. Say why rather than letting the list request come back
    // as a bare "Forbidden".
    if (session.self.role !== 'superadmin') {
        return (
            <DashboardErrorView error="Only superadmins can manage Staff Console access. Ask an engineer if you need someone added or removed." />
        )
    }

    if (error) {
        return <DashboardErrorView error={error} />
    }

    if (!rows) {
        return <DashboardLoadingDataView />
    }

    return (
        <DashboardLayout>
            <SnackbarProvider />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5">Staff Access</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Who can sign in to the Staff Console, and what they can
                        do.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddOpen(true)}
                >
                    Add someone
                </Button>
            </Stack>

            <Paper variant="outlined">
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    autoHeight
                    rowHeight={56}
                />
            </Paper>

            <Dialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add someone to the Staff Console</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={(e) =>
                                setForm({ ...form, email: e.target.value })
                            }
                            fullWidth
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="First name"
                                value={form.first_name}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        first_name: e.target.value,
                                    })
                                }
                                fullWidth
                            />
                            <TextField
                                label="Last name"
                                value={form.last_name}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        last_name: e.target.value,
                                    })
                                }
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            select
                            label="Role"
                            value={form.role}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    role: e.target.value as Role,
                                })
                            }
                            fullWidth
                        >
                            {ROLES.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        </TextField>
                        <DialogContentText variant="body2">
                            If they do not have a Habitat staff account yet, one
                            is created and they are emailed a link to set their
                            own password.
                        </DialogContentText>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                    <LoadingButton
                        variant="contained"
                        loading={busy}
                        disabled={!form.email.trim() || !form.first_name.trim()}
                        onClick={handleAdd}
                    >
                        Add
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!pendingRemoval}
                onClose={() => setPendingRemoval(null)}
            >
                <DialogTitle>Remove access?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingRemoval?.email} will lose access to the Staff
                        Console on their next request. Their Habitat account is
                        left alone — if they are leaving the company, it needs
                        deleting separately.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingRemoval(null)}>
                        Cancel
                    </Button>
                    <LoadingButton
                        color="error"
                        variant="contained"
                        loading={busy}
                        onClick={handleRemove}
                    >
                        Remove access
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    )
}
